import { useIntl } from 'react-intl';

import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import {
	filterMeasurementsForContext,
	findAllMeasurementsForFerryTrip,
	getMeasurementLabelFromId,
	getMeasurementValueFromId,
} from 'Util/_HumanWritten/MeasurementUtils';
import { useMeasurementFilterContext } from './Hooks/useMeasurementFilterContext';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { isVehicleBookingWizardData, VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { WizardErrors } from '../../BookingWizardTypes';
import { useVehicleTabContext } from './VehicleTabContext';

export function VehicleLengthInput() {
	const { cargoTypes, vehicleMeasurementPrices } = useVehicleTabContext();
	const intl = useIntl();
	const { wizardData, oldWizardData, setWizardData, innerWizardErrors, setInnerWizardErrors } =
		useBookingWizardDataStore();
	// Called before the early return below: hooks cannot be conditional.
	const filterContext = useMeasurementFilterContext();

	if (!isVehicleBookingWizardData(wizardData.innerWizardData)) {
		return <></>;
	}

	const typedErrors = innerWizardErrors as WizardErrors<VehicleBookingWizardData>;
	const data = wizardData.innerWizardData;

	const isDisabledOption = (optionId: string, minimumLengthId?: string) => {
		let lengthToUse = minimumLengthId;
		if (wizardData.restrictedMode && isNotNullOrUndefined(oldWizardData)) {
			lengthToUse = (oldWizardData.innerWizardData as VehicleBookingWizardData).vehicleLengthId;
		}

		if (minimumLengthId) {
			return (
				getMeasurementValueFromId(optionId) < getMeasurementValueFromId(lengthToUse) ||
				getMeasurementValueFromId(optionId) < getMeasurementValueFromId(minimumLengthId)
			);
		}
		return false;
	};

	const getVehicleLengthOptionDisplay = (optionId: string, isDisabled: boolean) => {
		const lengthPrice = vehicleMeasurementPrices[optionId] ?? 0;

		let message = '- vehicle too large';
		if (wizardData.restrictedMode && isNotNullOrUndefined(oldWizardData)) {
			const oldLengthId = (oldWizardData.innerWizardData as VehicleBookingWizardData).vehicleLengthId;
			if (getMeasurementValueFromId(optionId) < getMeasurementValueFromId(oldLengthId)) {
				message = '- restricted';
			}
		}

		const measurementLabel = getMeasurementLabelFromId(optionId);

		if (wizardData.isBulkBookingFlow) {
			return `${measurementLabel} ${isDisabled ? message : ''}`.trim();
		}

		const priceLabel = formatPriceDisplay(lengthPrice, wizardData.wizardMode === 'ALTERATION', true, true);

		return `${measurementLabel} (${priceLabel}) ${isDisabled ? message : ''}`.trim();
	};

	const minimumLengthId = cargoTypes.find(v => v.id === data.cargoTypeId)?.minimumLengthId;

	// The operator's per-measurement visibility rules, on top of the existing show-in-dropdown flag.
	// The current selection is always kept so an alteration of a booking made before a rule existed
	// can still render its own value — see useResetDisallowedMeasurements for when it is replaced.
	const visibleMeasurements = filterMeasurementsForContext(
		findAllMeasurementsForFerryTrip('LENGTH', 'vehicle'),
		filterContext.vehicle,
		[data.vehicleLengthId],
	);

	const options = visibleMeasurements.map(x => {
		const isDisabled = isDisabledOption(x.id, minimumLengthId);
		return {
			value: x.id,
			label: getVehicleLengthOptionDisplay(x.id, isDisabled),
			isDisabled,
		};
	});

	const selectedOption = options.find(o => o.value === data.vehicleLengthId) ?? null;

	const label = intl.formatMessage({
		id: 'vehicle-tab__vehicle-length__label',
		defaultMessage: 'Vehicle length',
	});
	const isReturnTrip = wizardData.tripType === 'return';
	const description = isReturnTrip
		? intl.formatMessage({
				id: 'vehicle-tab__vehicle-length__return-price-hint',
				defaultMessage: 'Price shown includes the return trip',
			})
		: undefined;

	return (
		<SelectInput
			className="vehicle-length"
			label={label}
			description={description}
			placeholder={intl.formatMessage({
				id: 'vehicle-tab__vehicle-length__placeholder',
				defaultMessage: 'Select vehicle length',
			})}
			options={options}
			value={selectedOption}
			required
			isSearchable
			scrollToFirstEnabledOption
			error={typedErrors?.vehicleLengthId}
			onChange={(option: any) => {
				const newData = new BookingWizardData({ ...wizardData });
				if (isVehicleBookingWizardData(newData.innerWizardData)) {
					newData.innerWizardData.vehicleLengthId = option?.value ?? '';
				}
				setWizardData(newData);

				const newErrors = { ...typedErrors };
				delete newErrors.vehicleLengthId;
				setInnerWizardErrors(newErrors);
			}}
		/>
	);
}
